import React from 'react';
import { Box, Typography } from '@mui/material';
import { GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid';
import DataGridMultilinePopper from '@/components/table/DataGridComponents/DataGridMultilinePopper';
import validationSchema from '@/components/dialogs/RenameFileDialog/validationSchema';
import { formatDate } from '@/utils/constants';
import { safelyParseDate } from '@/utils/date';
import { OwnerCellData } from '../OwnerCell';
import { FolderName } from './FolderName';
import AttachmentsOptions from './attachmentsOptions';

// Decodes HTML entities and strips HTML tags from a string
const decodeHtmlString = (text) => {
	if (!text || typeof text !== 'string') return text;

	// Use DOMParser to properly decode HTML entities and strip HTML tags
	return new DOMParser().parseFromString(text, 'text/html').body.textContent;
};

export const getAttachmentsColumns = ({ setActiveFolder, handlePreview, handleRename }) => [
	{
		...GRID_CHECKBOX_SELECTION_COL_DEF,
		hideable: false,
		disableColumnMenu: true,
		headerName: 'Checkbox',
	},
	{
		field: 'name',
		numeric: false,
		headerName: 'Document name',
		editable: true,
		hideable: false,
		disableColumnMenu: true,
		align: 'left',
		flex: 2,
		minWidth: 200,
		display: 'flex',
		renderCell: ({ row }) => <FolderName {...{ row, setActiveFolder, handlePreview }} />,
		renderEditCell: (params) => (
			<DataGridMultilinePopper
				{...params}
				maxLength={250}
				validationSchema={validationSchema}
				newLine={false}
			/>
		),
	},
	{
		field: 'download',
		headerName: 'Download',
		sortable: false,
		filterable: false,
		hideable: false,
		reorderable: false,
		disableColumnMenu: true,
		width: 25,
		renderHeader: () => <></>,
		renderCell: ({ row }) => {
			if (row.isFolder) return null;
			return (
				<AttachmentsOptions
					row={row}
					handlePreview={handlePreview}
					handleRename={handleRename}
				/>
			);
		},
	},
	{
		// field: 'lastModifiedTimeUnix',
		field: 'lastModifiedDateTime',
		sortable: true,
		headerName: 'Last modified',
		flex: 1,
		minWidth: 200,
		display: 'flex',
		renderCell: ({ row }) => (
			<Typography noWrap variant='body1'>
				{row.lastModifiedDateTime
					? formatDate(safelyParseDate(row.lastModifiedDateTime), true)
					: ''}
			</Typography>
		),
	},
	{
		field: 'description',
		headerName: 'Description',
		sortable: false,
		flex: 2,
		minWidth: 200,
		editable: true,
		display: 'flex',
		renderCell: ({ row }) => {
			const empty = (row.description ?? '').length === 0;
			const decodedDescription = decodeHtmlString(row.description);
			return (
				<Box component='span' sx={{ color: empty ? 'text.disabled' : undefined }}>
					{empty && !row.isFolder ? 'Please enter a description' : decodedDescription}
				</Box>
			);
		},
		renderEditCell: (params) => {
			const decodedValue = decodeHtmlString(params.value);
			return <DataGridMultilinePopper {...params} value={decodedValue} newLine={false} />;
		},
	},
	{
		// field: 'lastModifiedTimeUnix',
		field: 'sentDate',
		sortable: true,
		headerName: 'Sent/Received',
		flex: 1,
		minWidth: 200,
		display: 'flex',
		renderCell: ({ row }) => (
			<Typography noWrap variant='body1'>
				{row.sentDate ? formatDate(safelyParseDate(row.sentDate), true) : ''}
			</Typography>
		),
	},
	{
		field: 'fromEmail',
		// sortable: true,
		sortable: false,
		headerName: 'FROM',
		flex: 1,
		minWidth: 150,
		display: 'flex',
		renderCell: ({ row }) => (
			<Typography noWrap variant='body1'>
				{row.fromEmail ?? ''}
			</Typography>
		),
	},
	{
		field: 'toEmail',
		// sortable: true,
		sortable: false,
		headerName: 'To',
		flex: 1,
		minWidth: 150,
		display: 'flex',
		renderCell: ({ row }) => (
			<Typography noWrap variant='body1'>
				{row.toEmail ?? ''}
			</Typography>
		),
	},
	{
		field: 'createdDateTimeUnix',
		// sortable: true,
		sortable: false,
		headerName: 'Date added',
		flex: 1,
		minWidth: 200,
		display: 'flex',
		renderCell: ({ row }) => (
			<Typography noWrap variant='body1'>
				{row.createdDateTime ? formatDate(safelyParseDate(row.createdDateTime), true) : ''}
			</Typography>
		),
	},
	{
		field: 'createdBy',
		headerName: 'Uploaded by',
		sortable: true,
		flex: 1,
		minWidth: 200,
		display: 'flex',
		renderCell: ({ row }) => (
			<OwnerCellData
				email={row.createdBy?.user?.email || row.uploadedBy}
				fallbackName={row.createdBy?.user?.displayName}
				color='text.primary'
			/>
		),
	},
];
